import { clearGlobalQueryState } from "./api-client.js";

export type BrowserAccountChangePhase = "start" | "commit" | "cancel";

export interface BrowserAccountChangeEvent {
  version: 1;
  eventId: string;
  authScope: string;
  sourceTabId: string;
  phase: BrowserAccountChangePhase;
  targetAccountId: string | null;
  occurredAt: number;
}

export interface BrowserAccountChangeEffects {
  blockRender: () => void;
  freezeRequests: () => void;
  clearQueryCache: () => void;
  clearTransientState: () => void;
  reload: () => void;
  scheduleFallbackReload: () => void;
}

const CHANNEL_PREFIX = "dd-active-account-change.v1";
const HANDLED_PREFIX = "dd-active-account-handled.v1";
const EVENT_MAX_AGE_MS = 30_000;
let fallbackTabId: string | null = null;

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function accountScopedStorageKey(accountId: string, key: string): string {
  return `dd-account:${encodeURIComponent(accountId)}:${key}`;
}

export function accountChangeStorageKey(authScope: string): string {
  return `${CHANNEL_PREFIX}:${encodeURIComponent(authScope)}`;
}

function handledStorageKey(authScope: string): string {
  return `${HANDLED_PREFIX}:${encodeURIComponent(authScope)}`;
}

export function browserAccountTabId(): string {
  // Deliberately memory-only. Browsers can clone sessionStorage when a tab is
  // duplicated; a persisted tab ID would make the duplicate ignore the source
  // tab's PREPARE message. A fresh document must always be a distinct peer.
  fallbackTabId ??= randomId();
  return fallbackTabId;
}

export function createBrowserAccountChangeEvent(input: {
  authScope: string;
  sourceTabId: string;
  phase: BrowserAccountChangePhase;
  targetAccountId?: string | null;
  eventId?: string;
  occurredAt?: number;
}): BrowserAccountChangeEvent {
  return {
    version: 1,
    eventId: input.eventId ?? randomId(),
    authScope: input.authScope,
    sourceTabId: input.sourceTabId,
    phase: input.phase,
    targetAccountId: input.targetAccountId ?? null,
    occurredAt: input.occurredAt ?? Date.now(),
  };
}

export function publishBrowserAccountChange(event: BrowserAccountChangeEvent): boolean {
  let published = false;
  try {
    window.localStorage.setItem(accountChangeStorageKey(event.authScope), JSON.stringify(event));
    published = true;
  } catch {
    // BroadcastChannel remains available in browsers that block localStorage.
  }
  if (typeof BroadcastChannel !== "undefined") {
    try {
      const channel = new BroadcastChannel(accountChangeStorageKey(event.authScope));
      channel.postMessage(event);
      channel.close();
      published = true;
    } catch {
      // The caller fails closed when neither transport worked.
    }
  }
  return published;
}

export function subscribeBrowserAccountChanges(
  authScope: string,
  onEvent: (event: BrowserAccountChangeEvent) => void,
): () => void {
  const storageKey = accountChangeStorageKey(authScope);
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== storageKey || !event.newValue) return;
    const parsed = parseBrowserAccountChangeEvent(event.newValue);
    if (parsed) onEvent(parsed);
  };
  window.addEventListener("storage", onStorage);

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(storageKey);
      channel.addEventListener("message", (event: MessageEvent<unknown>) => {
        const parsed = parseBrowserAccountChangeEvent(event.data);
        if (parsed) onEvent(parsed);
      });
    } catch {
      channel = null;
    }
  }

  return () => {
    window.removeEventListener("storage", onStorage);
    channel?.close();
  };
}

export function readRecentBrowserAccountChange(
  authScope: string,
  now = Date.now(),
): BrowserAccountChangeEvent | null {
  try {
    const parsed = parseBrowserAccountChangeEvent(
      window.localStorage.getItem(accountChangeStorageKey(authScope)),
    );
    if (!parsed || now - parsed.occurredAt > EVENT_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function browserAccountEventHandled(
  authScope: string,
  eventId: string,
  storage: Storage | null = safeSessionStorage(),
): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(handledStorageKey(authScope)) === eventId;
  } catch {
    return false;
  }
}

export function markBrowserAccountEventHandled(
  authScope: string,
  eventId: string,
  storage: Storage | null = safeSessionStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(handledStorageKey(authScope), eventId);
  } catch {
    // A blocked session store can cause one extra reload, never cross-account data.
  }
}

export function applyRemoteBrowserAccountChange(input: {
  event: BrowserAccountChangeEvent;
  authScope: string;
  tabId: string;
  handled: boolean;
  effects: BrowserAccountChangeEffects;
}): boolean {
  const { event, authScope, tabId, handled, effects } = input;
  if (!browserAccountChangeBlocksRender(event, authScope, tabId, handled)) return false;

  // Order is deliberate: nothing may request or render after the shared active
  // account starts changing. The commit/cancel event then forces a clean boot.
  effects.blockRender();
  effects.freezeRequests();
  effects.clearQueryCache();
  effects.clearTransientState();
  if (event.phase === "start") effects.scheduleFallbackReload();
  else effects.reload();
  return true;
}

export function browserAccountChangeBlocksRender(
  event: BrowserAccountChangeEvent,
  authScope: string,
  tabId: string,
  handled: boolean,
): boolean {
  return event.authScope === authScope && event.sourceTabId !== tabId && !handled;
}

export function clearTenantBrowserTransientState(): void {
  clearGlobalQueryState();
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith("ccd.onboarding.")) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Reload still clears in-memory tenant state when browser storage is blocked.
  }
  try {
    // Never import the old unscoped values into an account. New scoped keys use
    // the `dd-account:` prefix and are intentionally retained for that account.
    window.localStorage.removeItem("dd-drill-presets-v1");
    window.localStorage.removeItem("ccd.costNag.dismissedDate");
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith("dd-page-filters-")) window.localStorage.removeItem(key);
    }
  } catch {
    // The old values are never read by the scoped code even if removal fails.
  }
}

export function parseBrowserAccountChangeEvent(value: unknown): BrowserAccountChangeEvent | null {
  let parsed: unknown = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed as Partial<BrowserAccountChangeEvent>;
  if (
    candidate.version !== 1
    || typeof candidate.eventId !== "string"
    || typeof candidate.authScope !== "string"
    || typeof candidate.sourceTabId !== "string"
    || !["start", "commit", "cancel"].includes(candidate.phase ?? "")
    || (candidate.targetAccountId !== null && typeof candidate.targetAccountId !== "string")
    || typeof candidate.occurredAt !== "number"
  ) {
    return null;
  }
  return candidate as BrowserAccountChangeEvent;
}

function safeSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
